import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import store from "store";
import styles from "./index.module.scss";
import useAsyncEffect from "@/hooks/useAsyncEffect";
import { waitRes } from "@/utils/axios";
import { del_apiKey, get_apiKeyList, get_businessList, post_addApiKey } from "@/api/uaapi/account";
import { Input, Checkbox, message, Tooltip } from "antd";
import SafeVerify from "@/components/App/SafeVerify";
import { axiosThrottle, deepClone } from "@/utils/method";
import { businesslist, get_businessIsForbid } from "@/api/xt-user";
import XtTable, { ColumnsType, XtTableRefProps } from "@/components/Xt/Table";
import ApiModal from "./ApiModal";
import NoApi from "./NoApi";
import Secret from "./Secret";
import { FaSceneDictEnum } from "@/store/user";
import ModelAlert from "@/components/Antd/Modal/Alert";
import { FaCheckProps } from "@/components/App/SafeVerify";
import { get_getFaCheckItem, get_userAccountApiKeyDelete, post_userAccountApiKeyAdd, post_userAccountApiKeyListCurr } from "@/api/uaapi/user";
type ApiKeyProps = {
  // 创建时间时间戳
  createTimeLong: number;
  /**创建时间字符串 */
  createTime: number | string;
  id: number;
  /**是否锁定 0否 1是 */
  isLock: number;
  /**公钥 */
  accessKey?: string;
  /**备注名 */
  keyName: string;
  /**权限 */
  roleScopes: string;
  /**白名单IP */
  bindIps: string;
};
interface ApiProps extends BaseCompProps {
  attr?: any;
}

export type CheckItemType = "QUERY" | "TRADE" | "WITHDRAW" | "LEVER_LOAN" | "FUTURE" | "TRANSFER";
interface AuthItem {
  name: CheckItemType;
  langKey: string;
}
const { useTranslation } = Hooks;
const businessCode = "2b3f5af3f615bae5";
const { moment } = Util;
export const initEditForm = {
  id: 0,
  keyName: "",
  roleScopes: [] as CheckItemType[],
  bindIps: "",
};
export type EditFormType = typeof initEditForm;
let faResultId = "";
const Api: React.FC<ApiProps> = ({ className }) => {
  const t = useTranslation();
  const isDark = store.app.theme?.toLowerCase() === "dark";
  const tableTheme = isDark ? "Dark" : "Light";
  const xtTableRef = useRef({} as XtTableRefProps);
  const [status, setStatus] = useState(false);
  const { notSub, isSubAcc, getVerifyItems } = store.user;
  const [isApi, setIsApi] = useState(false);
  const [form, setForm] = useState({ keyName: "", bindIps: "" });
  const [checkList, setCheckList] = useState<CheckItemType[]>(["QUERY"]);
  const [apiKeyList, setApiKeyList] = useState<ApiKeyProps[]>([]);
  const [keyMap, setKeyMap] = useState({ accessKey: "", secretKey: "" });
  const [editForm, setEditForm] = useState(deepClone(initEditForm));
  const [apiVisible, setApiVisible] = useState(false);
  const [secretOpen, setSecretOpen] = useState(false);
  const [faCheckData, setFaCheckData] = useState<FaCheckProps>({
    faCheckId: "",
    email: "",
    countryCode: "",
    mobile: "",
    faItems: [],
  });

  useEffect(() => {
    getFaCheckItem();
  }, []);

  const getFaCheckItem = async () => {
    const { data, err } = await waitRes(get_getFaCheckItem, {
      noHandle401: true,
      errorPop: true,
      params: { faBizType: 57 },
    });
    if (err) return;

    faResultId = data.faCheckId;
    setFaCheckData(data);
  };
  /**useEffect */
  useAsyncEffect(async () => {
    await getBusinesslist();
    // getApiKeyList();
    getBusinessIsForbid();
  }, []);

  /**useMemo */
  type ColumnsTypeAny = ColumnsType<ApiKeyProps>;
  const columns = useMemo<ColumnsTypeAny>(() => {
    return [
      {
        label: t("account.GenerationTime"),
        prop: "createTimeLong",
        flexWeight: 2.5,
        render(text, record) {
          return text ? moment(text).formatMs() : "";
        },
      },
      {
        label: t("account.AliasName") + "/" + t("account.PublicKey"),
        flexWeight: 4,
        render(text, record, index) {
          return (
            <>
              <p>{record.keyName}</p>
              <p>{record.accessKey}</p>
            </>
          );
        },
      },
      {
        label: t("account.Authority") + "/" + t("account.WhitelistIP"),
        flexWeight: 3,
        render(text, record, index) {
          return (
            <>
              <p>{showShell(record)}</p>
              <p>{record.bindIps}</p>
            </>
          );
        },
      },
      {
        label: t("app.status"),
        render(text, record, index) {
          return <span>{!record.isLock ? t("account.normal") : t("account.forbitten")}</span>;
        },
      },

      notSub
        ? {
            label: t("app.operation"),
            textAlign: "right",
            bodyCellstyle: { color: "#B9F78A" },
            render(text, record, index) {
              return (
                <div className={styles["operation_btn"]}>
                  <span onClick={() => editApiKey(record)}>{t("account.Modify")}</span>
                  <span onClick={() => delApi(record.id)}>{t("newAccount.delete")}</span>
                </div>
              );
            },
          }
        : {},
    ];
  }, []).filter((item) => Object.keys(item));
  /**method */
  const showShell = (row: ApiKeyProps) => {
    const result = [t("account.QueryAuthority")];
    const authlist: AuthItem[] = [
      {
        name: "TRADE",
        langKey: "account.TradingAuthority",
      },
      {
        name: "WITHDRAW",
        langKey: "account.WithdrawalAuthority",
      },
      {
        name: "LEVER_LOAN",
        langKey: "account.LeverLoanAuthority",
      },
      {
        name: "FUTURE",
        langKey: "account.FutureAuthority",
      },
      {
        name: "TRANSFER",
        langKey: "account.TransferAuthority",
      },
    ];
    authlist.forEach((auth) => {
      if (row.roleScopes.indexOf(auth.name) != -1) {
        result.push(t(auth.langKey));
      }
    });
    return result.join(",");
  };
  const getBusinesslist = async () => {
    const { data, err }: { data: { businessCode: string; status: number }[]; err?: string } = await waitRes(businesslist, {});
    if (err || !data.length) return;
    const bool = data.some((item) => item.businessCode === businessCode);
    setIsApi(bool);
  };
  // 判断是否禁止创建
  const getBusinessIsForbid = async () => {
    const { data, err } = await waitRes(get_businessIsForbid, { params: { businessCode } });
    if (err) return;
    setStatus(!data);
  };
  // 获取api key 列表
  // const getApiKeyList = async () => {
  //   const { data, err } = await waitRes(get_apiKeyList);
  //   if (err) return;
  //   setApiKeyList(data);
  // };

  // 修改api key
  const editApiKey = async (record: ApiKeyProps) => {
    // 返填form 数据
    const { keyName = "", roleScopes, bindIps = "", id } = record;
    const roleScopesArr = roleScopes.split(",") as CheckItemType[];
    const data = { keyName, roleScopes: roleScopesArr, bindIps, id };
    await getFaCheckItem();
    setEditForm(data);
    setApiVisible(true);
  };

  // 删除api
  const delApi = async (id: number) => {
    ModelAlert.confirm({
      center: true,
      content: t("account.deleteTheAPI"),
      okText: t("account.Confirm"),
      cancelText: t("account.cancel"),
      onOk: async () => {
        const { data, err } = await waitRes(get_userAccountApiKeyDelete, {
          successPop: true,
          errorPop: true,
          params: {
            id,
          },
        });
        if (err) return;
        xtTableRef.current?.initPageQuery(true);
      },
    });
  };
  const submitForm = axiosThrottle(async (unlock, res) => {
    if (!status) return message.error(t("account.noToCreat"));
    const roleScopes = checkList.join(",");
    console.log(roleScopes, "roleScopes");
    const { data, err } = await waitRes(post_userAccountApiKeyAdd, {
      errorPop: true,
      successPop: true,
      data: { faResultId: res.faResultId, ...form, roleScopes },
    });
    unlock();
    await getFaCheckItem();
    if (err) return;
    data && setKeyMap(data);
    setSecretOpen(true);
    xtTableRef.current?.initPageQuery(true);
    setForm({ keyName: "", bindIps: "" });
    setCheckList(["QUERY"]);
    await getBusinesslist();
  });
  return (
    <div className={cx(styles.account_api, className)}>
      {notSub && (
        <div className={styles["api_form_header"]}>
          <div className={styles["api_header"]}>
            <h6>{t("account.CreateAPI")}</h6>
          </div>
          {isApi && (
            <div className={styles["api_form_group"]}>
              <div className={styles["col-md-6"]}>
                <div className={styles["form_group"]}>
                  <div className={styles["form_container"]}>
                    <div>
                      <p className={styles["form_t_l"]}>{t("account.AliasName")}</p>
                      <Input
                        className={styles["api_form_input"]}
                        placeholder={t("account.AliasName")}
                        value={form.keyName}
                        onChange={(e) => setForm({ ...form, keyName: e.target.value })}
                      ></Input>
                    </div>
                    <div>
                      <p className={styles["form_t_l"]}>
                        {t("account.WhitelistIP")} ({t("account.selectenter")})
                      </p>
                      <Input
                        className={styles["api_form_input"]}
                        placeholder={t("account.WhitelistIP")}
                        value={form.bindIps}
                        onChange={(e) => setForm({ ...form, bindIps: e.target.value })}
                      ></Input>
                    </div>
                    <div className={styles["checkbox_group"]}>
                      <span className={styles["label"]}>{t("account.Authority")}</span>
                      <Checkbox.Group
                        className={styles["api_checkbox"]}
                        value={checkList}
                        onChange={(checkedValues) => setCheckList(checkedValues as CheckItemType[])}
                      >
                        <Checkbox value={"QUERY"} className={styles["checkbox_list"]} disabled>
                          {t("account.QueryAuthority")}
                        </Checkbox>
                        <Checkbox value={"TRADE"} className={styles["checkbox_list"]}>
                          {t("account.TradingAuthority")}
                        </Checkbox>
                        <Tooltip placement="top" title={t("account.WithdrawalAuthorityTip")}>
                          <Checkbox value={"WITHDRAW"} className={styles["checkbox_list"]}>
                            {t("account.WithdrawalAuthority")}
                          </Checkbox>
                        </Tooltip>
                        <Tooltip placement="top" title={t("account.LeverLoanAuthorityTip")}>
                          <Checkbox value={"LEVER_LOAN"} className={styles["checkbox_list"]}>
                            {t("account.LeverLoanAuthority")}
                          </Checkbox>
                        </Tooltip>
                        <Tooltip placement="top" title={t("account.FutureAuthorityTip")}>
                          <Checkbox value={"FUTURE"} className={styles["checkbox_list"]}>
                            {t("account.FutureAuthority")}
                          </Checkbox>
                        </Tooltip>
                        <Tooltip placement="top" title={t("account.TransferAuthorityTip")}>
                          <Checkbox value={"TRANSFER"} className={styles["checkbox_list"]}>
                            {t("account.TransferAuthority")}
                          </Checkbox>
                        </Tooltip>
                      </Checkbox.Group>
                    </div>

                    <SafeVerify faCheckData={faCheckData} showLink={true} faBizType={57} dynamicCodeType={"USER_2024"} handleSuccessFn={submitForm} mt={20} />
                  </div>
                </div>
              </div>

              <div className={cx(styles["col-md-6"], styles["api_apply"])}>
                <dl>
                  <dt>{t("account.APIApplicationGuide")}</dt>
                  <dt>* {t("account.allowedToBeGenerated")}</dt>
                  <dt>* {t("account.IPsWillBeAllowed")}</dt>
                  <dt>* {t("account.APIPrivateKey")}</dt>
                  <dt>
                    * {t("account.pleaseReferTo")}
                    <a target="_blank" href="https://doc.bona-bit.com/" className={styles["api_document"]} rel="noreferrer">
                      {t("account.APIDocumentation")}
                    </a>
                  </dt>
                </dl>
              </div>
            </div>
          )}
        </div>
      )}

      {(isSubAcc || isApi) && (
        <div className={styles["api_table"]}>
          <div className={styles["table_title"]}>
            <h6 style={{ fontFamily: "DIN-Bold, DIN", fontWeight: 400, color: "#fff" }}>{t("account.APIList")}</h6>
          </div>
          <div className={styles["account_table"]}>
            <XtTable
              ref={xtTableRef}
              columns={columns}
              minHeight={0}
              fetchMethod={post_userAccountApiKeyListCurr}
              interceptors={{}}
              isPagination={false}
              theme={tableTheme}
              headerRowBgColor={isDark ? "#1b1b1e" : "#fff"}
              headerRowColor={isDark ? "rgba(255, 255, 255, 0.6)" : "#b9b9ba"}
              rowHoverColor={isDark ? "rgba(255, 255, 255, 0.02)" : "#fefdf2"}
            />
          </div>
        </div>
      )}

      {notSub && !isApi && <NoApi />}

      <ApiModal
        title={t("account.Modify") + "API"}
        visible={apiVisible}
        form={editForm}
        onFormChange={(values) => setEditForm({ ...editForm, ...values })}
        closeApiModal={(bool) => {
          setApiVisible(false);
          bool && xtTableRef.current.initPageQuery(true);
        }}
      ></ApiModal>
      <Secret closeSecret={() => setSecretOpen(false)} keyMap={keyMap} title={`API${t("account.getsuccess")}`} open={secretOpen} />
    </div>
  );
};
export default observer(Api);
